import { useSepaStatus } from '@/hooks/useBilling';
import { Modal } from '@/components/ui/Modal';
import { SepaMandateSetup } from '@/components/Billing';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SepaGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showModal?: boolean;
  strict?: boolean;
}

export const SepaGuard: React.FC<SepaGuardProps> = ({
  children,
  fallback,
  showModal = true,
  strict = false,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSepaStatus();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Error state - allow access but show warning
  if (error) {
    console.warn('Failed to load SEPA status:', error);
    return <>{children}</>;
  }

  // Un moyen de paiement enregistré (carte, ou mandat SEPA déjà signé), un
  // compte dispensé (`exempt`, rendu avec hasValidMandate=true) ou une période
  // de grâce ouvrent l'accès. En mode strict, la période de grâce ne compte pas.
  const hasAccess = strict
    ? data?.hasValidMandate === true
    : data?.hasValidMandate || data?.isInGracePeriod;

  if (hasAccess) {
    return <>{children}</>;
  }

  // User doesn't have SEPA mandate
  if (showModal) {
    // Wrap children with a click-blocking overlay
    return (
      <>
        <div className="relative">
          {/* Render children with pointer-events disabled */}
          <div className="pointer-events-none">{children}</div>

          {/* Invisible overlay that intercepts all clicks */}
          <div
            className="absolute inset-0 cursor-pointer bg-transparent transition-colors hover:bg-neutral-900/5"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            aria-label={t(
              'billing.sepa.required.clickToSetup',
              'Cliquez pour enregistrer votre carte bancaire'
            )}
          />
        </div>

        {/* Keep modal mounted to prevent re-initialization */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={t(
            'billing.sepa.required.title',
            'Carte bancaire à enregistrer'
          )}
          size="xl"
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Colonne gauche : Pédagogie */}
            <div className="space-y-4">
              {/* Description principale */}
              <p className="text-sm leading-relaxed text-neutral-700">
                {data?.canManage === false
                  ? t(
                      'billing.sepa.required.memberDescription',
                      "Pour utiliser les fonctionnalités d'automatisation, le cabinet doit avoir enregistré une carte bancaire."
                    )
                  : t(
                      'billing.sepa.required.description',
                      "Pour utiliser les fonctionnalités d'automatisation, enregistrez une carte bancaire : vos factures seront réglées automatiquement."
                    )}
              </p>

              {/* Tarification */}
              <div className="bg-primary-50 border-primary-200 rounded-xl border p-4">
                <div className="flex items-start gap-3">
                  <div className="bg-primary-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                    <span className="text-primary-700 text-lg font-bold">
                      €
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-primary-900 mb-2 text-sm font-semibold">
                      {t(
                        'billing.sepa.pricing.title',
                        'Tarification simple et transparente'
                      )}
                    </h4>
                    <p className="text-primary-800 text-xs leading-relaxed">
                      {t(
                        'billing.sepa.pricing.description',
                        "1€ HT par dossier utilisé dans vos automatisations, quel que soit le nombre d'exécutions. Un dossier utilisé = un dossier facturé, peu importe combien de fois il s'exécute dans le mois."
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Exemples */}
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
                <h4 className="mb-3 text-xs font-medium text-neutral-900">
                  {t('billing.sepa.pricing.examples.title', 'Exemples :')}
                </h4>
                <ul className="space-y-2 text-xs text-neutral-700">
                  <li className="flex items-start gap-2">
                    <span className="text-success-600 mt-0.5 font-bold">✓</span>
                    <span>
                      {t(
                        'billing.sepa.pricing.examples.one',
                        '5 dossiers avec automatisation = 5€ HT/mois'
                      )}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success-600 mt-0.5 font-bold">✓</span>
                    <span>
                      {t(
                        'billing.sepa.pricing.examples.two',
                        "1 dossier qui s'exécute 30 fois = 1€ HT/mois"
                      )}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success-600 mt-0.5 font-bold">✓</span>
                    <span>
                      {t(
                        'billing.sepa.pricing.examples.three',
                        '10 dossiers avec multiples automatisations = 10€ HT/mois'
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Colonne droite : formulaire de carte (ou explication pour un
                simple membre, rendue par SepaMandateSetup) */}
            <div className="lg:border-l lg:border-neutral-200 lg:pl-6">
              <SepaMandateSetup />
            </div>
          </div>
        </Modal>
      </>
    );
  }

  // Custom fallback UI
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default fallback - don't render children
  return null;
};
